/**
 * Recommendation Tracking Service
 *
 * Lightweight service for recording recommendation interactions.
 * Uses fire-and-forget pattern to avoid blocking UI.
 *
 * Signal strength hierarchy (for future feedback learning):
 * - Strong positive: completed, approved
 * - Medium positive: generated_brief
 * - Weak positive: clicked, expanded, viewed
 * - Negative: dismissed
 */

import { supabase } from "@/lib/supabaseClient";

import type { SupabaseClient } from "@supabase/supabase-js";

// ============================================================================
// TYPES
// ============================================================================

export type RecommendationEventType =
  | "viewed"
  | "clicked"
  | "expanded"
  | "generated_brief"
  | "dismissed"
  | "approved"
  | "completed"
  | "decision_brief_viewed"
  | "next_move_clicked"
  | "next_move_completed"
  | "next_move_helpful"
  | "next_move_not_helpful";

export interface TrackingContext {
  sessionId: string;
  pathname: string;
  recommendationRank?: number;
  ctaType?: string;
  [key: string]: unknown;
}

export interface RecommendationInteractionInput {
  recommendationId: string;
  clientId: string;
  eventType: RecommendationEventType;
  sourceComponent?: string;
  // Denormalized fields for cheaper analysis
  recommendationCategory?: string;
  agentType?: string;
  // Rich context
  context?: Partial<TrackingContext>;
}

export interface CategoryInteractionSummary {
  category: string;
  totalRecommendations: number;
  totalViewed: number;
  totalClicked: number;
  totalApproved: number;
  totalDismissed: number;
  totalCompleted: number;
  approvalRate: number;
  completionRate: number;
}

// ============================================================================
// SESSION ID (generated once per page load)
// ============================================================================

const SESSION_ID =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// ============================================================================
// DEDUPE LAYER (in-memory, client-side)
// ============================================================================

const recentEventKeys = new Map<string, number>();
const DEDUPE_WINDOW_MS = 500;
const MAX_CACHE_SIZE = 200;

function getDedupeKey(input: RecommendationInteractionInput): string {
  return `${input.recommendationId}:${input.eventType}`;
}

function shouldDedupe(input: RecommendationInteractionInput): boolean {
  const key = getDedupeKey(input);
  const now = Date.now();
  const lastSeen = recentEventKeys.get(key);

  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
    return true;
  }

  recentEventKeys.set(key, now);

  // Clean up old entries if cache is too large
  if (recentEventKeys.size > MAX_CACHE_SIZE) {
    const entries = Array.from(recentEventKeys.entries());
    const cutoff = now - DEDUPE_WINDOW_MS * 2;
    entries.forEach(([k, timestamp]) => {
      if (timestamp < cutoff) {
        recentEventKeys.delete(k);
      }
    });
  }

  return false;
}

// ============================================================================
// CORE TRACKING FUNCTION
// ============================================================================

/**
 * Record an interaction event.
 * Fire-and-forget: does not await the database insert.
 * User ID is inferred by the database via auth.uid().
 */
export function trackRecommendationInteraction(
  input: RecommendationInteractionInput,
  client: SupabaseClient = supabase,
): void {
  // Dedupe rapid-fire events
  if (shouldDedupe(input)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug(
        "[RecTracking] Deduped:",
        input.eventType,
        input.recommendationId.slice(0, 8),
      );
    }
    return;
  }

  // Build full context with session info
  const eventContext: TrackingContext = {
    sessionId: SESSION_ID,
    pathname:
      typeof window !== "undefined" ? window.location.pathname : "/unknown",
    ...input.context,
  };

  // Dev logging
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(
      "[RecTracking]",
      input.eventType,
      input.recommendationId.slice(0, 8),
      eventContext.recommendationRank != null
        ? `rank=${eventContext.recommendationRank}`
        : "",
    );
  }

  // Fire-and-forget insert
  // Note: user_id is set by DB default (auth.uid()), not passed from client
  void client
    .from("recommendation_interactions")
    .insert({
      recommendation_id: input.recommendationId,
      client_id: input.clientId,
      event_type: input.eventType,
      recommendation_category: input.recommendationCategory ?? null,
      agent_type: input.agentType ?? null,
      source_component: input.sourceComponent ?? null,
      event_context: eventContext,
    })
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.warn("[RecTracking] Insert failed:", error.message);
      }
    });
}

// ============================================================================
// BATCH TRACKING (for viewed events with IntersectionObserver)
// ============================================================================

interface PendingViewEvent {
  recommendationId: string;
  clientId: string;
  recommendationCategory?: string;
  agentType?: string;
  sourceComponent?: string;
  context?: Partial<TrackingContext>;
}

const pendingViewedEvents: PendingViewEvent[] = [];
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
const FLUSH_DELAY_MS = 1000;
const FLUSH_BATCH_SIZE = 10;

/**
 * Queue a "viewed" event for batched insertion.
 * Flushes after 1 second of inactivity or when batch reaches 10 items.
 */
export function queueViewedEvent(input: PendingViewEvent): void {
  // Check dedupe before queueing
  const dedupeInput: RecommendationInteractionInput = {
    ...input,
    eventType: "viewed",
  };
  if (shouldDedupe(dedupeInput)) {
    return;
  }

  pendingViewedEvents.push(input);

  // Flush if batch is large enough
  if (pendingViewedEvents.length >= FLUSH_BATCH_SIZE) {
    flushViewedEvents();
    return;
  }

  // Schedule flush after delay
  if (flushTimeoutId) {
    clearTimeout(flushTimeoutId);
  }
  flushTimeoutId = setTimeout(flushViewedEvents, FLUSH_DELAY_MS);
}

function flushViewedEvents(): void {
  if (pendingViewedEvents.length === 0) return;

  const batch = pendingViewedEvents.splice(0, pendingViewedEvents.length);

  if (flushTimeoutId) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[RecTracking] Flushing", batch.length, "viewed events");
  }

  // Build rows with session context
  const rows = batch.map((input) => ({
    recommendation_id: input.recommendationId,
    client_id: input.clientId,
    event_type: "viewed" as const,
    recommendation_category: input.recommendationCategory ?? null,
    agent_type: input.agentType ?? null,
    source_component: input.sourceComponent ?? null,
    event_context: {
      sessionId: SESSION_ID,
      pathname:
        typeof window !== "undefined" ? window.location.pathname : "/unknown",
      ...input.context,
    },
  }));

  // Fire-and-forget batch insert
  void supabase
    .from("recommendation_interactions")
    .insert(rows)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.warn("[RecTracking] Batch insert failed:", error.message);
      }
    });
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Get category interaction summary for feedback learning.
 * Returns counts of distinct recommendations per event type.
 */
export async function getCategoryInteractionSummary(
  clientId: string,
  options: { fromDate?: string; toDate?: string } = {},
  client: SupabaseClient = supabase,
): Promise<CategoryInteractionSummary[]> {
  const { data, error } = await client.rpc(
    "fn_get_category_interaction_summary",
    {
      p_client_id: clientId,
      p_from_date: options.fromDate ?? null,
      p_to_date: options.toDate ?? null,
    },
  );

  if (error) {
    console.error("[RecTracking] Failed to get category summary:", error);
    throw error;
  }

  return (data ?? []).map((row: Record<string, unknown>) => ({
    category: (row.category as string) ?? "unknown",
    totalRecommendations: (row.total_recommendations as number) ?? 0,
    totalViewed: (row.total_viewed as number) ?? 0,
    totalClicked: (row.total_clicked as number) ?? 0,
    totalApproved: (row.total_approved as number) ?? 0,
    totalDismissed: (row.total_dismissed as number) ?? 0,
    totalCompleted: (row.total_completed as number) ?? 0,
    approvalRate: (row.approval_rate as number) ?? 0,
    completionRate: (row.completion_rate as number) ?? 0,
  }));
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Clear the dedupe cache (useful for testing).
 */
export function clearDedupeCache(): void {
  recentEventKeys.clear();
}

/**
 * Get the current session ID.
 */
export function getSessionId(): string {
  return SESSION_ID;
}

/**
 * Flush any pending viewed events immediately.
 */
export function flushPendingEvents(): void {
  flushViewedEvents();
}

/**
 * Convenience function for tracking decision brief views.
 * Fires when a user opens the Decision Engine brief panel.
 */
export function trackDecisionBriefViewed(
  clientId: string,
  primaryActionId: string,
  primaryConfidence: string,
  secondaryCount: number,
): void {
  trackRecommendationInteraction({
    recommendationId: primaryActionId,
    clientId,
    eventType: "decision_brief_viewed",
    context: {
      primaryConfidence,
      secondaryCount,
    },
  });
}

// ============================================================================
// NEXT MOVE TRACKING (Decision Loop)
// ============================================================================

export interface NextMoveClickedInput {
  actionId: string;
  clientId: string;
  nextMoveType: "navigate" | "inline" | "trigger";
  targetRoute: string;
  urgency: string;
  confidence: string;
  actionSubjectType: string;
}

/**
 * Track when a user clicks a NextMove recommendation or CTA.
 * Fires before navigation to capture the intent.
 */
export function trackNextMoveClicked(
  input: NextMoveClickedInput,
  client: SupabaseClient = supabase,
): void {
  trackRecommendationInteraction(
    {
      recommendationId: input.actionId,
      clientId: input.clientId,
      eventType: "next_move_clicked",
      sourceComponent: "actions_page",
      context: {
        source: "actions_page",
        sourceType: "attention_signal_action",
        nextMoveType: input.nextMoveType,
        targetRoute: input.targetRoute,
        urgency: input.urgency,
        confidence: input.confidence,
        actionSubjectType: input.actionSubjectType,
      },
    },
    client,
  );
}

export interface NextMoveCompletedInput {
  actionId: string;
  clientId: string;
  outcome: string;
  hadPriorClick: boolean;
  timeToComplete: number | null;
  notes?: string;
}

/**
 * Track when an action is marked resolved.
 * hadPriorClick indicates whether the user engaged with the NextMove first.
 */
export function trackNextMoveCompleted(
  input: NextMoveCompletedInput,
  client: SupabaseClient = supabase,
): void {
  trackRecommendationInteraction(
    {
      recommendationId: input.actionId,
      clientId: input.clientId,
      eventType: "next_move_completed",
      sourceComponent: "actions_page",
      context: {
        source: "actions_page",
        outcome: input.outcome,
        hadPriorClick: input.hadPriorClick,
        timeToComplete: input.timeToComplete,
        notes: input.notes,
      },
    },
    client,
  );
}

export interface NextMoveFeedbackInput {
  actionId: string;
  clientId: string;
  urgency: string;
  actionSubjectType: string;
  hadDecisionFrame: boolean;
}

/**
 * Track thumbs-up feedback on a recommendation.
 */
export function trackNextMoveHelpful(
  input: NextMoveFeedbackInput,
  client: SupabaseClient = supabase,
): void {
  trackRecommendationInteraction(
    {
      recommendationId: input.actionId,
      clientId: input.clientId,
      eventType: "next_move_helpful",
      sourceComponent: "actions_page",
      context: {
        source: "actions_page",
        urgency: input.urgency,
        actionSubjectType: input.actionSubjectType,
        hadDecisionFrame: input.hadDecisionFrame,
      },
    },
    client,
  );
}

/**
 * Track thumbs-down feedback on a recommendation.
 */
export function trackNextMoveNotHelpful(
  input: NextMoveFeedbackInput,
  client: SupabaseClient = supabase,
): void {
  trackRecommendationInteraction(
    {
      recommendationId: input.actionId,
      clientId: input.clientId,
      eventType: "next_move_not_helpful",
      sourceComponent: "actions_page",
      context: {
        source: "actions_page",
        urgency: input.urgency,
        actionSubjectType: input.actionSubjectType,
        hadDecisionFrame: input.hadDecisionFrame,
      },
    },
    client,
  );
}
